import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useTenant } from "@/contexts/TenantContext";
import { api } from "@/lib/api";
import type { ContentSlotData, SlotPosition } from "./ContentSlot";

/**
 * Extended slot data with targeting info (for type convenience)
 */
export type TargetedSlotData = ContentSlotData;

/**
 * Check if current path matches a route pattern
 * Supports: exact match, single wildcard (*), double wildcard (**)
 */
function matchRoute(pattern: string, path: string): boolean {
  // Normalize paths
  const normalizedPattern = pattern.replace(/\/+$/, "") || "/";
  const normalizedPath = path.replace(/\/+$/, "") || "/";

  // Exact match
  if (normalizedPattern === normalizedPath) return true;

  // Global match
  if (normalizedPattern === "*" || normalizedPattern === "**") return true;

  // Pattern with wildcards
  if (normalizedPattern.includes("*")) {
    const regexPattern = normalizedPattern
      .replace(/\*\*/g, "{{DOUBLE}}")  // Temporarily replace **
      .replace(/\*/g, "[^/]*")          // Single * = one segment
      .replace(/{{DOUBLE}}/g, ".*");    // ** = any segments

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(normalizedPath);
  }

  return false;
}

/**
 * Hook to fetch all active slots and filter by current route
 */
export function usePageSlots(position?: SlotPosition) {
  const { getToken } = useAuth();
  const { tenantId } = useTenant();
  const location = useLocation();
  const currentPath = location.pathname;

  const query = useQuery({
    queryKey: ["pageSlots", tenantId, position],
    queryFn: async () => {
      const token = await getToken();
      // Fetch all active slots (we'll filter client-side for flexibility)
      const data = await api.get<{ slots: TargetedSlotData[] }>(
        "/api/content-slots/active",
        token || undefined
      );
      return data.slots || [];
    },
    staleTime: 2 * 60_000, // Cache for 2 minutes
    enabled: !!tenantId,
  });

  // Filter slots for current page and position
  const filteredSlots = (query.data || []).filter((slot) => {
    const { targetPages, excludePages, position: slotPosition } = slot.content;

    // Filter by position - slot must have a matching position to show
    // If requesting a specific position, only show slots that have that position set
    if (position) {
      if (!slotPosition || slotPosition !== position) {
        return false;
      }
    }

    // Check exclusions first
    if (excludePages?.some((pattern) => matchRoute(pattern, currentPath))) {
      return false;
    }

    // If no target pages specified, show on all pages (for position match)
    if (!targetPages || targetPages.length === 0) {
      return true;
    }

    // Check if current path matches any target pattern
    return targetPages.some((pattern) => matchRoute(pattern, currentPath));
  });

  return {
    ...query,
    slots: filteredSlots,
  };
}

/**
 * Default renderer for injected slots
 */
function SlotRenderer({ slot }: { slot: TargetedSlotData }) {
  const { tenant } = useTenant();
  const content = slot.content;
  const bgColor = content.backgroundColor || tenant.colors?.primary || "#1f2937";
  const txtColor = content.textColor || "#ffffff";

  switch (content.type) {
    case "announcement":
      return (
        <div
          className="px-4 py-2 text-center text-sm"
          style={{ backgroundColor: bgColor, color: txtColor }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            {content.heading && <span className="font-medium">{content.heading}</span>}
            {content.text && <span className="opacity-90">{content.text}</span>}
            {content.ctaText && content.ctaUrl && (
              <a href={content.ctaUrl} className="font-medium underline hover:no-underline">
                {content.ctaText}
              </a>
            )}
          </div>
        </div>
      );

    case "banner":
      return (
        <div
          className="relative overflow-hidden"
          style={{ backgroundColor: bgColor, color: txtColor }}
        >
          {content.imageUrl && (
            <img
              src={content.imageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
          )}
          <div className="relative max-w-7xl mx-auto px-6 py-12 text-center">
            {content.heading && (
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{content.heading}</h2>
            )}
            {content.subheading && (
              <p className="text-xl opacity-90 mb-4">{content.subheading}</p>
            )}
            {content.text && <p className="opacity-80 mb-6">{content.text}</p>}
            {content.ctaText && content.ctaUrl && (
              <a
                href={content.ctaUrl}
                className="inline-block px-8 py-3 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors"
              >
                {content.ctaText}
              </a>
            )}
          </div>
        </div>
      );

    case "promo":
      return (
        <div
          className="max-w-7xl mx-auto px-6 py-8"
        >
          <div
            className="rounded-xl p-6 flex items-center gap-6"
            style={{ backgroundColor: bgColor, color: txtColor }}
          >
            {content.imageUrl && (
              <img src={content.imageUrl} alt="" className="w-24 h-24 object-cover rounded-lg flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              {content.heading && <h3 className="text-xl font-bold mb-1">{content.heading}</h3>}
              {content.subheading && <p className="text-sm opacity-80">{content.subheading}</p>}
              {content.text && <p className="text-sm opacity-70 mt-2">{content.text}</p>}
            </div>
            {content.ctaText && content.ctaUrl && (
              <a
                href={content.ctaUrl}
                className="px-5 py-2.5 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                {content.ctaText}
              </a>
            )}
          </div>
        </div>
      );

    case "cta":
      return (
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          {content.heading && <h3 className="text-2xl font-bold mb-3">{content.heading}</h3>}
          {content.text && <p className="text-gray-600 mb-6">{content.text}</p>}
          {content.ctaText && content.ctaUrl && (
            <a
              href={content.ctaUrl}
              className="inline-block px-8 py-3 font-semibold rounded-lg transition-colors"
              style={{ backgroundColor: bgColor, color: txtColor }}
            >
              {content.ctaText}
            </a>
          )}
        </div>
      );

    default:
      return null;
  }
}

interface SlotInjectorProps {
  /** Position to render slots for */
  position: SlotPosition;
  /** Additional CSS classes */
  className?: string;
  /** Custom render function */
  render?: (slots: TargetedSlotData[]) => React.ReactNode;
}

/**
 * SlotInjector - Automatically renders content slots for the current page and position
 *
 * Place this component at injection points in your layout:
 *
 * @example
 * ```tsx
 * <SlotInjector position="announcement" />
 * <Navigation />
 * <SlotInjector position="hero" />
 * <main>{children}</main>
 * <SlotInjector position="above-footer" />
 * <Footer />
 * ```
 */
export function SlotInjector({ position, className = "", render }: SlotInjectorProps) {
  const { slots, isLoading } = usePageSlots(position);

  if (isLoading || slots.length === 0) {
    return null;
  }

  if (render) {
    return <div className={className}>{render(slots)}</div>;
  }

  return (
    <div className={className}>
      {slots.map((slot) => (
        <SlotRenderer key={slot.id} slot={slot} />
      ))}
    </div>
  );
}

export default SlotInjector;
